import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/csv";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CategoryManager from "@/components/CategoryManager";
import AdminProducts from "@/pages/AdminProducts";
import AdminTeam from "@/pages/AdminTeam";
import AdminDashboardHome from "@/pages/AdminDashboardHome";
import AdminActionCenter from "@/pages/AdminActionCenter";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminInspectionQueue from "@/pages/AdminInspectionQueue";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminOrders from "@/pages/AdminOrders";
import AdminAgreements from "@/pages/AdminAgreements";
import { useAuth } from "@/context/AuthContext";

const ORDER_STATUSES = [
  "pending_payment", "ordered", "dispatched", "delivered",
  "return_requested", "return_shipped", "return_received",
  "return_vendor_approved", "return_vendor_rejected", "return_disputed",
  "return_admin_approved", "return_admin_rejected", "refunded", "credit_issued", "cancelled",
];

function InspectDialog({ open, onClose, product, onSaved }) {
  const [f, setF] = useState({
    authenticity_pass: true, quality_pass: true, size_fit_pass: true,
    skeptic_notes: "", perfectionist_notes: "", softie_notes: "",
  });
  const save = async () => {
    const { data } = await api.post(`/admin/products/${product.id}/certify`, f);
    toast.success(data.passed ? "Product certified & approved" : "Product failed inspection");
    onSaved(); onClose();
  };
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Listing Inspection</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">{product.title}</DialogTitle>
          <p className="font-mono text-[11px] text-muted-foreground">{product.vendor_name}</p>
        </DialogHeader>
        <div className="space-y-5 mt-4">
          {[
            { k: "authenticity_pass", n: "skeptic_notes", label: "The Skeptic · Authenticity" },
            { k: "quality_pass", n: "perfectionist_notes", label: "The Perfectionist · Quality" },
            { k: "size_fit_pass", n: "softie_notes", label: "The Softie · Fit & Comfort" },
          ].map((row) => (
            <div key={row.k} className="border border-foreground/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[10px] uppercase tracking-widest">{row.label}</p>
                <div className="flex gap-2">
                  {[true, false].map((v) => (
                    <button key={String(v)} data-testid={`insp-${row.k}-${v}`} onClick={() => setF({ ...f, [row.k]: v })}
                      className={`px-3 py-1 border text-xs font-mono uppercase ${f[row.k] === v ? (v ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--accent))]" : "bg-destructive text-destructive-foreground border-destructive") : "border-foreground/40"}`}>
                      {v ? "PASS" : "FAIL"}
                    </button>
                  ))}
                </div>
              </div>
              <textarea data-testid={`notes-${row.k}`} placeholder="Notes..." value={f[row.n]} onChange={(e) => setF({ ...f, [row.n]: e.target.value })} className="underline-input min-h-[60px] w-full" />
            </div>
          ))}
          <button data-testid="submit-inspection" onClick={save} className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">
            Certify Product
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorUsername({ vendor, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(vendor.username || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/vendors/${vendor.id}/username`, { username: val });
      toast.success("Username set");
      setEditing(false);
      onSaved && onSaved(data.username);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set username"); }
    finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <button onClick={() => { setVal(vendor.username || ""); setEditing(true); }} className="font-mono text-xs hover:text-[hsl(var(--accent))]">
        {vendor.username || <span className="text-muted-foreground">— set —</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input value={val} onChange={(e) => setVal(e.target.value)} autoCapitalize="none" placeholder="username" className="border border-foreground bg-background px-1.5 py-1 text-xs w-28" />
      <button disabled={busy || !val.trim()} onClick={save} className="text-[10px] font-mono uppercase bg-foreground text-background px-2 py-1 disabled:opacity-50">Save</button>
      <button onClick={() => setEditing(false)} className="text-[10px] font-mono uppercase border border-foreground px-2 py-1">×</button>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const can = (p) => user?.admin_roles?.includes("super_admin") || (user?.permissions || []).includes(p);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [inviteLink, setInviteLink] = useState(null); // { business_name, url }
  const [regCred, setRegCred] = useState(null); // { email, password, slug } after activation
  const [newInvite, setNewInvite] = useState({ business_name: "", contact_name: "", email: "", mobile: "" });
  const [inspecting, setInspecting] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ vendor_id: "", period_start: "", period_end: "", gross: 0, commission: 0 });

  const load = async () => {
    const [s, v, p, o, b, py, rv, eq] = await Promise.all([
      api.get("/admin/stats").then(r => r.data),
      api.get("/admin/vendors").then(r => r.data),
      api.get("/admin/products").then(r => r.data),
      api.get("/admin/orders").then(r => r.data),
      api.get("/admin/buyers").then(r => r.data),
      api.get("/admin/payouts").then(r => r.data),
      api.get("/admin/reviews").then(r => r.data),
      api.get("/admin/vendor-enquiries").then(r => r.data),
    ]);
    setStats(s); setVendors(v); setProducts(p); setOrders(o); setBuyers(b); setPayouts(py); setReviews(rv); setEnquiries(eq);
    api.get("/admin/vendor-registrations").then(r => setRegistrations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.post(`/admin/vendor-enquiries/${id}/status`, { status });
      toast.success(`Enquiry marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  const inviteToRegister = async (enq) => {
    try {
      const { data } = await api.post("/admin/vendor-invites", {
        enquiry_id: enq.id,
        business_name: enq.business_name,
        contact_name: enq.contact_name,
        email: enq.email,
        mobile: enq.mobile,
      });
      const url = `${window.location.origin}/vendor-register?token=${data.token}`;
      setInviteLink({ business_name: enq.business_name, url });
      try { await navigator.clipboard.writeText(url); toast.success("Invite link generated & copied"); }
      catch { toast.success("Invite link generated"); }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create invite"); }
  };
  const createDirectInvite = async () => {
    if (!newInvite.business_name.trim()) { toast.error("Business name is required"); return; }
    try {
      const { data } = await api.post("/admin/vendor-invites", { ...newInvite });
      const url = `${window.location.origin}/vendor-register?token=${data.token}`;
      setInviteLink({ business_name: newInvite.business_name, url });
      setNewInvite({ business_name: "", contact_name: "", email: "", mobile: "" });
      try { await navigator.clipboard.writeText(url); toast.success("Invite link generated & copied"); }
      catch { toast.success("Invite link generated"); }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create invite"); }
  };
  const setRegStatus = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/vendor-registrations/${id}/status`, { status });
      if (status === "approved") {
        toast.success(`Approved · Vendor ID ${data.vendor_code}`);
        if (data.temp_password) {
          setRegCred({ email: data.login_email, password: data.temp_password, slug: data.vendor_slug });
        } else if (data.vendor_slug) {
          setRegCred({ email: data.login_email, password: null, slug: data.vendor_slug });
        }
      } else {
        toast.success(`Marked ${status.replace(/_/g, " ")}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  const approveVendor = async (id) => { await api.post(`/admin/vendors/${id}/approve`); toast.success("Vendor approved"); load(); };
  const rejectVendor = async (id) => { await api.post(`/admin/vendors/${id}/reject`); toast.success("Rejected"); load(); };
  const changeOrderStatus = async (id, status) => { await api.post(`/admin/orders/${id}/status`, { status }); toast.success("Updated"); load(); };
  const createPayout = async () => {
    if (!payoutForm.vendor_id || !payoutForm.period_start || !payoutForm.period_end) { toast.error("Fill all fields"); return; }
    await api.post("/admin/payouts", {
      ...payoutForm, gross: Number(payoutForm.gross), commission: Number(payoutForm.commission),
    });
    toast.success("Payout created"); setPayoutForm({ vendor_id: "", period_start: "", period_end: "", gross: 0, commission: 0 });
    load();
  };
  const markPaid = async (id) => { await api.post(`/admin/payouts/${id}/mark-paid`); toast.success("Marked paid"); load(); };
  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${id}`);
    toast.success("Review deleted"); load();
  };
  const adminReturnFinal = async (id, approve) => {
    const notes = window.prompt(approve ? "Approval notes for buyer:" : "Rejection notes for buyer:", "") || "";
    await api.post(`/admin/orders/${id}/return-final`, { approve, notes });
    toast.success(approve ? "Return approved by admin" : "Return rejected by admin");
    load();
  };
  const completeReturn = async (id, method) => {
    await api.post(`/admin/orders/${id}/complete-return`, { method });
    toast.success(method === "refund" ? "Refund initiated" : "Store credit issued");
    load();
  };
  const createShipment = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/create-shipment`);
      toast.success(`Shipment ${data.mocked ? "simulated" : "created"} · AWB ${data.awb}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Shipment create failed");
    }
  };
  const cancelShipment = async (id) => {
    if (!window.confirm("Cancel this shipment with Shiprocket?")) return;
    try {
      await api.post(`/admin/orders/${id}/cancel-shipment`);
      toast.success("Shipment cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    }
  };
  const [tracking, setTracking] = useState(null); // { order, data }
  const openTracking = async (order) => {
    try {
      const { data } = await api.get(`/orders/${order.id}/tracking`);
      setTracking({ order, data });
    } catch (e) {
      toast.error("Failed to fetch tracking");
    }
  };
  const [refundConfirm, setRefundConfirm] = useState(null); // order awaiting refund approval
  const confirmRefund = async () => {
    if (!refundConfirm) return;
    try {
      const { data } = await api.post(`/admin/orders/${refundConfirm.id}/complete-return`, { method: "refund" });
      if (data.processor === "razorpay") {
        toast.success(`Razorpay refund initiated · ${data.refund_id}`);
      } else if (data.processor === "mock") {
        toast.success("Order marked refunded (mock payment)");
      } else {
        toast.success("Refund initiated");
      }
      setRefundConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const vendorById = useMemo(() => {
    const m = {};
    for (const v of vendors) m[v.id] = v;
    return m;
  }, [vendors]);

  const TABS = ["overview", "action_center", "insights", "enquiries", "registrations", "categories", "vendors", "agreements", "products",
    ...(can("product.inspect") ? ["inspection_queue"] : []),
    "orders", "buyers", "payouts", "reviews",
    ...(can("team.manage") ? ["team"] : []),
    ...(can("audit.view") ? ["audit_log"] : [])];
  const ENQUIRY_STATUSES = ["new", "contacted", "qualified", "invited", "rejected", "closed"];
  const REG_STATUSES = ["submitted", "under_verification", "documents_pending", "commercial_review", "approved", "rejected"];

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Spotters Hub · Admin Console</p>
      <h1 className="font-serif text-6xl mt-2 mb-8 tracking-tighter">Inspection Floor.</h1>

      <div className="flex gap-4 md:gap-6 border-b border-foreground mb-8 font-mono text-[11px] uppercase tracking-widest overflow-x-auto">
        {TABS.map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`py-3 border-b-2 transition-colors whitespace-nowrap ${tab === t ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent hover:text-[hsl(var(--accent))]"}`}>
            {t.replace(/[-_]/g, " ")}
          </button>
        ))}
      </div>

      {tab === "overview" && <AdminDashboardHome onGo={setTab} />}
      {tab === "action_center" && <AdminActionCenter onGo={setTab} />}
      {tab === "insights" && <AdminAnalytics />}

      {tab === "categories" && <CategoryManager />}

      {tab === "enquiries" && (
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Vendor Enquiries · {enquiries.length}
            </p>
            <p className="font-mono text-[10px] text-muted-foreground">
              {enquiries.filter(e => e.status === "new").length} new
            </p>
          </div>
          {inviteLink && (
            <div className="mb-5 border-2 border-[hsl(var(--accent))] p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Registration link · {inviteLink.business_name} (copied)</p>
              <div className="mt-2 flex items-center gap-3">
                <input readOnly value={inviteLink.url} onFocus={(e) => e.target.select()} className="flex-1 font-mono text-[11px] bg-secondary px-3 py-2 border border-foreground/20" />
                <button onClick={() => { navigator.clipboard?.writeText(inviteLink.url); toast.success("Copied"); }} className="font-mono text-[10px] uppercase tracking-widest border border-foreground px-3 py-2 hover:bg-foreground hover:text-background">Copy</button>
                <button onClick={() => setInviteLink(null)} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dismiss</button>
              </div>
            </div>
          )}
          {enquiries.length === 0 && (
            <p className="font-mono text-sm text-muted-foreground py-8">No enquiries yet.</p>
          )}
          <div className="space-y-4">
            {enquiries.map(e => (
              <div key={e.id} className="border border-foreground/30 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="font-serif text-2xl tracking-tighter">{e.business_name}</p>
                      <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
                        e.status === "new" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                          : e.status === "rejected" ? "border-destructive text-destructive"
                          : "border-foreground/40 text-muted-foreground"}`}>
                        {e.status}
                      </span>
                    </div>
                    <p className="font-mono text-[11px] text-muted-foreground mt-1">
                      {e.vendor_type} · {e.city} · {new Date(e.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right font-mono text-[11px]">
                    <p>{e.contact_name}</p>
                    <p className="text-muted-foreground">{e.mobile}</p>
                    {e.email && <p className="text-muted-foreground">{e.email}</p>}
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Products:</span> {e.products}</p>
                  {e.brands && <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Brands:</span> {e.brands}</p>}
                  <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Looking for:</span> {e.looking_for}</p>
                  {e.website && <p className="truncate"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Link:</span> {e.website}</p>}
                </div>
                {e.about && <p className="mt-3 text-sm text-muted-foreground border-l-2 border-foreground/20 pl-3">{e.about}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Set status:</span>
                  {ENQUIRY_STATUSES.map(s => (
                    <button key={s} data-testid={`enq-status-${e.id}-${s}`} onClick={() => setEnquiryStatus(e.id, s)}
                      className={`px-2.5 py-1 border font-mono text-[10px] uppercase tracking-wide ${
                        e.status === s ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>
                      {s}
                    </button>
                  ))}
                  <button data-testid={`enq-invite-${e.id}`} onClick={() => inviteToRegister(e)}
                    className="ml-auto px-3 py-1 font-mono text-[10px] uppercase tracking-widest bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-foreground hover:text-background">
                    Invite to register →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "registrations" && (
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-4">
            Vendor Registrations · {registrations.length}
          </p>

          {/* Direct invite — for vendors sourced offline who never submitted an enquiry */}
          <div className="mb-6 border border-foreground/30 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Invite a vendor directly · no enquiry needed</p>
            <div className="mt-3 grid md:grid-cols-4 gap-3">
              <input data-testid="direct-invite-business" placeholder="Business name *" value={newInvite.business_name}
                onChange={(e) => setNewInvite({ ...newInvite, business_name: e.target.value })} className="underline-input" />
              <input data-testid="direct-invite-contact" placeholder="Contact name" value={newInvite.contact_name}
                onChange={(e) => setNewInvite({ ...newInvite, contact_name: e.target.value })} className="underline-input" />
              <input data-testid="direct-invite-email" placeholder="Email" value={newInvite.email}
                onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })} className="underline-input" />
              <input data-testid="direct-invite-mobile" placeholder="Mobile" value={newInvite.mobile}
                onChange={(e) => setNewInvite({ ...newInvite, mobile: e.target.value })} className="underline-input" />
            </div>
            <button data-testid="direct-invite-submit" onClick={createDirectInvite}
              className="mt-4 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-6 py-2.5 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background">
              Generate invite link →
            </button>
          </div>

          {inviteLink && (
            <div className="mb-5 border-2 border-[hsl(var(--accent))] p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Registration link · {inviteLink.business_name} (copied)</p>
              <div className="mt-2 flex items-center gap-3">
                <input readOnly value={inviteLink.url} onFocus={(e) => e.target.select()} className="flex-1 font-mono text-[11px] bg-secondary px-3 py-2 border border-foreground/20" />
                <button onClick={() => { navigator.clipboard?.writeText(inviteLink.url); toast.success("Copied"); }} className="font-mono text-[10px] uppercase tracking-widest border border-foreground px-3 py-2 hover:bg-foreground hover:text-background">Copy</button>
                <button onClick={() => setInviteLink(null)} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dismiss</button>
              </div>
            </div>
          )}
          {regCred && (
            <div className="mb-5 border-2 border-[hsl(var(--accent))] p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendor activated · share these credentials securely</p>
              <div className="mt-2 grid md:grid-cols-3 gap-3 items-end">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Login email</p>
                  <p className="font-mono text-sm">{regCred.email || "—"}</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Temporary password</p>
                  <p className="font-mono text-sm">{regCred.password || "(existing account — password unchanged)"}</p>
                </div>
                <div className="flex items-center gap-3">
                  {regCred.slug && (
                    <a href={`/vendor/${regCred.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[10px] uppercase tracking-widest border border-foreground px-3 py-2 hover:bg-foreground hover:text-background">View storefront ↗</a>
                  )}
                  <button onClick={() => setRegCred(null)} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dismiss</button>
                </div>
              </div>
            </div>
          )}
          {registrations.length === 0 && (
            <p className="font-mono text-sm text-muted-foreground py-8">
              No registrations yet. Invite a vendor from the Enquiries tab to generate a registration link.
            </p>
          )}
          <div className="space-y-5">
            {registrations.map(r => {
              const docs = [
                ["PAN", r.pan_doc], ["GST", r.gst_doc], ["UDYAM", r.udyam_doc],
                ["Other Licence", r.other_licenses_doc], ["Brand Auth", r.brand_auth_doc],
                ["Catalogue", r.catalogue_doc], ["Price List", r.price_list_doc],
                ["Cancelled Cheque", r.cancelled_cheque_doc],
              ].filter(([, v]) => v);
              return (
                <div key={r.id} className="border border-foreground/30 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-serif text-2xl tracking-tighter">{r.legal_name}</p>
                        {r.vendor_code && (
                          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-foreground text-background">{r.vendor_code}</span>
                        )}
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
                          r.status === "approved" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                            : r.status === "rejected" ? "border-destructive text-destructive"
                            : "border-foreground/40 text-muted-foreground"}`}>
                          {(r.status || "").replace(/_/g, " ")}
                        </span>
                      </div>
                      <p className="font-mono text-[11px] text-muted-foreground mt-1">
                        {r.trade_name && r.trade_name !== r.legal_name ? `${r.trade_name} · ` : ""}{r.constitution} · {r.nature_of_business}
                      </p>
                    </div>
                    <div className="text-right font-mono text-[11px]">
                      <p>{r.contact_person} · {r.designation}</p>
                      <p className="text-muted-foreground">{r.mobile} · {r.email}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
                    <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">PAN:</span> {r.pan || "—"}</p>
                    <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">GST:</span> {r.gstin || r.gst_status || "—"}</p>
                    <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">UDYAM:</span> {r.udyam_number || r.udyam_status || "—"}</p>
                    <p className="md:col-span-3"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Categories:</span> {(r.product_categories || []).join(", ") || "—"}</p>
                    <p className="md:col-span-2"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Bank:</span> {r.bank_name ? `${r.account_holder} · ${r.bank_name} · A/C ${r.account_number} · ${r.ifsc}` : "—"}</p>
                    <p><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Terms:</span> {r.payment_terms || "—"} · {r.freight_terms || "—"}</p>
                  </div>

                  {docs.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Docs:</span>
                      {docs.map(([label, val]) => (
                        <a key={label} href={val} target="_blank" rel="noreferrer"
                          className="px-2.5 py-1 border border-foreground/30 font-mono text-[10px] uppercase tracking-wide hover:border-foreground">
                          {label} ↗
                        </a>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Pipeline:</span>
                    {REG_STATUSES.map(s => (
                      <button key={s} data-testid={`reg-status-${r.id}-${s}`} onClick={() => setRegStatus(r.id, s)}
                        className={`px-2.5 py-1 border font-mono text-[10px] uppercase tracking-wide ${
                          r.status === s
                            ? (s === "approved" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--accent))]"
                              : s === "rejected" ? "bg-destructive text-destructive-foreground border-destructive"
                              : "bg-foreground text-background border-foreground")
                            : "border-foreground/30 hover:border-foreground"}`}>
                        {s.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "vendors" && (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest">
            <th className="text-left py-2">Store</th><th className="text-left">Username</th><th className="text-left">Email</th><th className="text-left">Mobile</th><th>Status</th><th>Failure</th><th></th>
          </tr></thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.id} className="border-b border-foreground/20">
                <td className="py-3"><p className="font-serif text-lg">{v.store_name}</p><p className="text-[11px] font-mono text-muted-foreground">{v.slug}</p></td>
                <td><VendorUsername vendor={v} onSaved={load} /></td>
                <td className="font-mono text-xs break-all">{v.login_email || <span className="text-muted-foreground">—</span>}</td>
                <td className="font-mono text-xs">{v.phone || <span className="text-muted-foreground">—</span>}</td>
                <td className="font-mono text-xs uppercase">{v.status}</td>
                <td className="font-mono text-xs">{(v.failure_rate * 100).toFixed(1)}%</td>
                <td className="text-right">
                  {v.status === "pending" && (
                    <>
                      <button data-testid={`approve-vendor-${v.id}`} onClick={() => approveVendor(v.id)} className="text-xs font-mono uppercase text-[hsl(var(--accent))] mr-3">Approve</button>
                      <button data-testid={`reject-vendor-${v.id}`} onClick={() => rejectVendor(v.id)} className="text-xs font-mono uppercase text-destructive">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {tab === "products" && <AdminProducts onInspect={setInspecting} />}

      {tab === "team" && <AdminTeam />}

      {tab === "inspection_queue" && <AdminInspectionQueue />}

      {tab === "audit_log" && <AdminAuditLog />}

      {tab === "agreements" && <AdminAgreements />}
      {tab === "orders" && <AdminOrders />}

      {tab === "buyers" && (
        <div>
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{buyers.length} buyer{buyers.length === 1 ? "" : "s"}</p>
          <button onClick={() => {
            const rows = buyers.map((b) => ({
              Name: b.name, Email: b.email, Phone: b.phone ? `+91 ${b.phone}` : "",
              Location: b.location || "", Orders: b.order_count || 0,
              "Lifetime Spend (INR)": b.lifetime_spend || 0,
              Joined: b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN") : "",
            }));
            downloadCSV(`buyers-${new Date().toISOString().slice(0, 10)}.csv`, rows) || toast.error("No buyers to export");
          }} className="border border-foreground px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background">Export CSV</button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest">
            <th className="text-left py-2">Name</th><th>Email</th><th className="text-left">Location</th><th>Orders</th><th>Lifetime Spend</th><th>Joined</th>
          </tr></thead>
          <tbody>
            {buyers.map(b => (
              <tr key={b.id} className="border-b border-foreground/20">
                <td className="py-3 font-serif">{b.name}</td>
                <td className="font-mono text-xs">{b.email}</td>
                <td className="font-mono text-xs">{b.location || <span className="text-muted-foreground">—</span>}</td>
                <td className="font-mono">{b.order_count}</td>
                <td className="font-mono">₹ {(b.lifetime_spend || 0).toLocaleString("en-IN")}</td>
                <td className="font-mono text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString("en-IN")}</td>
              </tr>
            ))}
            {buyers.length === 0 && <tr><td colSpan={6} className="py-8 text-muted-foreground">No buyers yet.</td></tr>}
          </tbody>
        </table>
        </div>
        </div>
      )}

      {tab === "payouts" && (
        <div>
          <div className="border border-foreground p-6 mb-8">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Create Payout</p>
            <div className="grid md:grid-cols-6 gap-3">
              <select data-testid="payout-vendor" value={payoutForm.vendor_id} onChange={(e) => setPayoutForm({ ...payoutForm, vendor_id: e.target.value })} className="border border-foreground bg-background px-2 py-2 text-xs">
                <option value="">Select vendor</option>
                {vendors.filter(v => v.status === "approved").map(v => <option key={v.id} value={v.id}>{v.store_name}</option>)}
              </select>
              <input data-testid="payout-start" type="date" value={payoutForm.period_start} onChange={(e) => setPayoutForm({ ...payoutForm, period_start: e.target.value })} className="border border-foreground bg-background px-2 py-2 text-xs" />
              <input data-testid="payout-end" type="date" value={payoutForm.period_end} onChange={(e) => setPayoutForm({ ...payoutForm, period_end: e.target.value })} className="border border-foreground bg-background px-2 py-2 text-xs" />
              <input data-testid="payout-gross" type="number" placeholder="Gross ₹" value={payoutForm.gross} onChange={(e) => setPayoutForm({ ...payoutForm, gross: e.target.value })} className="border border-foreground bg-background px-2 py-2 text-xs" />
              <input data-testid="payout-comm" type="number" placeholder="Commission ₹" value={payoutForm.commission} onChange={(e) => setPayoutForm({ ...payoutForm, commission: e.target.value })} className="border border-foreground bg-background px-2 py-2 text-xs" />
              <button data-testid="payout-create" onClick={createPayout} className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase hover:bg-[hsl(var(--accent))]">Create</button>
            </div>
          </div>

          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest">
              <th className="text-left py-2">Vendor</th><th>Period</th><th>Gross</th><th>Commission</th><th>Net</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {payouts.map(p => {
                const v = vendorById[p.vendor_id];
                return (
                  <tr key={p.id} className="border-b border-foreground/20">
                    <td className="py-3 font-serif">{v?.store_name || p.vendor_id.slice(0, 8)}</td>
                    <td className="font-mono text-xs">{p.period_start} → {p.period_end}</td>
                    <td className="font-mono">₹ {p.gross.toLocaleString("en-IN")}</td>
                    <td className="font-mono text-muted-foreground">₹ {p.commission.toLocaleString("en-IN")}</td>
                    <td className="font-mono font-semibold">₹ {p.net.toLocaleString("en-IN")}</td>
                    <td><span className={`font-mono text-[10px] uppercase px-2 py-1 border ${p.status === "paid" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-foreground/40"}`}>{p.status}</span></td>
                    <td className="text-right">
                      {p.status === "pending" && (
                        <button data-testid={`mark-paid-${p.id}`} onClick={() => markPaid(p.id)} className="text-xs font-mono uppercase text-[hsl(var(--accent))]">Mark Paid</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {payouts.length === 0 && <tr><td colSpan={7} className="py-8 text-muted-foreground">No payouts yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "reviews" && (
        <div>
          <p className="text-sm text-muted-foreground mb-4">
            All customer reviews across the platform. Delete inappropriate, spammy, or fraudulent reviews.
          </p>
          {reviews.length === 0 ? (
            <p className="font-serif text-2xl text-muted-foreground mt-6">No reviews yet.</p>
          ) : (
            <div className="grid gap-3">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-foreground p-5 grid md:grid-cols-[1fr_auto] gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={i <= r.rating ? "text-[hsl(var(--accent))]" : "text-foreground/25"}>★</span>
                        ))}
                      </div>
                      {r.verified_purchase && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] border border-[hsl(var(--accent))] px-1.5 py-0.5">✓ Verified</span>
                      )}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString("en-IN")}
                      </span>
                    </div>
                    <p className="font-serif text-xl tracking-tighter">{r.title}</p>
                    <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{r.body}</p>
                    {r.images?.length > 0 && (
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {r.images.map((src, i) => (
                          <a key={i} href={src} target="_blank" rel="noreferrer" className="w-12 h-12 bg-secondary overflow-hidden">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                      by {r.user_name} · on <span className="text-foreground">{r.product_title}</span> ({r.vendor_name})
                    </div>
                  </div>
                  <div className="flex md:flex-col items-end gap-2">
                    <button
                      data-testid={`admin-del-review-${r.id}`}
                      onClick={() => deleteReview(r.id)}
                      className="border border-destructive text-destructive px-3 py-1.5 text-xs font-mono uppercase hover:bg-destructive hover:text-destructive-foreground"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <InspectDialog open={!!inspecting} onClose={() => setInspecting(null)} product={inspecting} onSaved={load} />

      {/* Refund approval dialog */}
      <Dialog open={!!refundConfirm} onOpenChange={() => setRefundConfirm(null)}>
        <DialogContent className="max-w-lg border-2 border-foreground rounded-none bg-background">
          <DialogHeader>
            <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Refund Approval · Real money will move</p>
            <DialogTitle className="font-serif text-3xl tracking-tighter">Approve this refund?</DialogTitle>
          </DialogHeader>
          {refundConfirm && (
            <div className="mt-4 space-y-3 text-sm">
              <div className="border border-foreground p-4 tag-punch pl-10 space-y-2 font-mono text-xs">
                <div className="grid grid-cols-[130px_1fr] gap-2">
                  <span className="text-muted-foreground uppercase tracking-widest">Order</span>
                  <span>{refundConfirm.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="grid grid-cols-[130px_1fr] gap-2">
                  <span className="text-muted-foreground uppercase tracking-widest">Buyer</span>
                  <span>{refundConfirm.buyer_name}</span>
                </div>
                <div className="grid grid-cols-[130px_1fr] gap-2">
                  <span className="text-muted-foreground uppercase tracking-widest">Payment ID</span>
                  <span className="break-all">{refundConfirm.payment_id || "—"}</span>
                </div>
                <div className="grid grid-cols-[130px_1fr] gap-2">
                  <span className="text-muted-foreground uppercase tracking-widest">Razorpay Order</span>
                  <span className="break-all">{refundConfirm.razorpay_order_id || "—"}</span>
                </div>
                <div className="grid grid-cols-[130px_1fr] gap-2 pt-2 border-t border-foreground/20">
                  <span className="uppercase tracking-widest font-semibold">Refund Amount</span>
                  <span className="font-semibold text-lg text-[hsl(var(--accent))]">₹ {refundConfirm.total.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {refundConfirm.payment_id?.startsWith("mock_")
                  ? "This order was paid via mock payment. No real refund will be issued — status will simply flip to Refunded."
                  : "Clicking Approve will call Razorpay's Refund API and initiate a real refund to the buyer's original payment method (5–7 working days per Razorpay)."}
              </p>
              <div className="flex gap-2">
                <button data-testid="refund-cancel" onClick={() => setRefundConfirm(null)}
                  className="flex-1 border border-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">
                  Cancel
                </button>
                <button data-testid="refund-approve" onClick={confirmRefund}
                  className="flex-1 bg-destructive text-destructive-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground">
                  Approve & Process Refund
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

